import { useCallback, useRef, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import {
  useSuspenseStocksQuery,
  useAnalyzeReceiptMutation,
  useConfirmReceiptIntakeMutation,
} from '../../queries';
import type { ReceiptIntakeItem, StockIntakePayload } from '../../entity';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';

export type StockAiIntakeFormValues = {
  items: (ReceiptIntakeItem & {
    itemType: 'MATERIAL' | 'GOODS';
    threshold: number;
    retailPrice: number;
    brand: string;
  })[];
};

const compressImage = (imageData: string, maxWidth = 1400, quality = 0.7): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = imageData;
  });

export const useStockAiIntake = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: stocks } = useSuspenseStocksQuery();
  const stockId = stocks?.[0]?.id;
  const { trackEvent } = useAnalytics();

  const methods = useForm<StockAiIntakeFormValues>({
    defaultValues: { items: [] },
  });

  const { fields, replace, remove } = useFieldArray({
    control: methods.control,
    name: 'items',
  });

  const { mutate: analyzeReceipt, isPending: isAnalyzing } = useAnalyzeReceiptMutation({
    onSuccess: result => {
      replace(
        result.map(item => ({
          ...item,
          itemType: 'MATERIAL',
          threshold: 0,
          retailPrice: 0,
          brand: '',
        })),
      );
      setIsDialogOpen(true);
    },
  });

  const { mutate: confirmIntake, isPending: isConfirming } = useConfirmReceiptIntakeMutation({
    onSuccess: () => {
      setIsDialogOpen(false);
      methods.reset();
    },
  });

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';

      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = async e => {
        const rawImage = e.target?.result as string;
        const compressed = await compressImage(rawImage);
        analyzeReceipt({ imageData: compressed, stockId });
      };
      reader.readAsDataURL(file);
    },
    [analyzeReceipt, stockId],
  );

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
    methods.reset();
  }, [methods]);

  const confirm = useCallback(
    (formData: StockAiIntakeFormValues) => {
      if (!stockId) return;

      const payloads: StockIntakePayload[] = formData.items.map(item => {
        if (item.matchedStockItemId) {
          return {
            id: item.matchedStockItemId,
            quantity: Number(item.quantity),
            totalPrice: Number(item.totalPrice),
            packageCount: Number(item.packageCount),
            stockId,
          };
        }

        return {
          itemName: item.itemName,
          unit: item.unit,
          quantity: Number(item.quantity),
          totalPrice: Number(item.totalPrice),
          packageCount: Number(item.packageCount),
          threshold: Number(item.threshold),
          avgUnitPrice: 0,
          itemType: item.itemType,
          retailPrice: Number(item.retailPrice),
          brand: item.brand,
          stockId,
        };
      });

      confirmIntake(payloads);
      trackEvent('ai_stock_intake_completed', {
        items_count: payloads.length,
        stock_id: stockId,
      });
    },
    [stockId, confirmIntake, trackEvent],
  );

  return {
    fileInputRef,
    fields,
    methods,
    isDialogOpen,
    isAnalyzing,
    isConfirming,
    openFilePicker,
    handleFileSelect,
    remove,
    closeDialog,
    confirm,
  };
};
